import { useTranslation } from "react-i18next";
import {
  ArrowLeftOutlined,
  DownOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Flex,
  Form,
  Layout,
  TableColumnsType,
  TableProps,
} from "antd";
import moment from "moment";
import { KeyboardEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormItemInput from "../../../components/form-input/FormInput";
import TableData from "../../../components/table-data/TableData";
import { routesConfig } from "../../../config/routes";
import { DEFAULT_PAGE_SIZE } from "../../../constants";
import { ScoringStatusExam } from "../../../constants/scoring";
import { Permission } from "../../../layout/sidebar/permission.enum";
import { getExamSessions } from "../../../service/scoring";
import { SessionInfo } from "../../../types/scoring-course";
import { CheckRole, getPermissions } from "../../../utils/checkRole";
import FilterSearch, { FilterSearchValues } from "./FilterSearch";
import "./style.scss";
import { RenderRecordedResults } from "../../../utils/functionUltils";

interface CourseScoringListProps {
  title?: string;
}

function ScoringList({ title }: CourseScoringListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, blockId } = useParams();
  const [scoreConfig, setScoreConfig] = useState<any>(null)
  const courseName = location.state?.courseName || scoreConfig?.courseName;
  const unitName = location.state?.unitName || scoreConfig?.name;

  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [hoverSortMessage, setHoverSortMessage] = useState<{
    triggerDesc: string;
    triggerAsc: string;
  }>({
    triggerDesc: t("Sắp xếp theo thứ tự Z-A", "Sắp xếp theo thứ tự Z-A"),
    triggerAsc: t("Sắp xếp thứ tự A-Z", "Sắp xếp thứ tự A-Z"),
  });

  //filter
  const [sort, setSort] = useState<string[]>();
  const [searchValue, setSearchValue] = useState("");
  const [isShowFilter, setIsShowFilter] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<number>();
  const [teacherFilter, setTeacherFilter] = useState<number>();
  const [date, setDate] = useState<{
    start: string;
    end: string;
  }>();
 

  const [displayRoleAction, setDisplayRoleAction] = useState(false);

  const [listPermission, setListPermission] = useState([]);

  const getRoles = async () => {
    const params = await getPermissions();
    const listPermissionConvert = JSON.parse(JSON.stringify(params));
    setListPermission(listPermissionConvert);
  };

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    const roleAction = [Permission.AutomatedExamGradingUpdate];

    const checkRoleView = CheckRole(roleAction, listPermission);
    if (checkRoleView.includes(true)) {
      setDisplayRoleAction(true);
    }
  }, [listPermission]);

  useEffect(() => {
    getExamSession();
  }, [statusFilter, teacherFilter, date, pageSize, currentPage, sort]);

  const getExamSession = async () => {
    try {
      setLoading(true);
      const res = await getExamSessions({
        blockId: Number(blockId),
        page: currentPage,
        size: pageSize,
        status: statusFilter,
        markUserId: teacherFilter,
        studentUsername: searchValue?.trim(),
        start: date?.start,
        end: date?.end,
        sort: sort,
      });
      setTotalElements(res.data.data.total);
      setSessions(res.data.data.data);
      setScoreConfig(res.data.data.uniConfig);
    } catch (err) {
      console.error;
    } finally {
      setLoading(false);
    }
  };

  const handleChangeInput = (e: any) => {
    const { value } = e.target;
    setSearchValue(value);
  };

  const handleSubmitSearch = () => {
    getExamSession();
  };

  const handleSearch = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter") {
      handleSubmitSearch();
    }
  };

  const handleFilter = (value: FilterSearchValues) => {
    setDate({
      start:
        value.date && value.date[0] ? value.date[0].toISOString() : undefined,
      end:
        value.date && value.date[1] ? value.date[1].toISOString() : undefined,
    });
    setStatusFilter(value.status ? value.status : undefined);
    setTeacherFilter(value.teacher ? value.teacher : undefined);
  };

  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const sortField =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
          ? "desc"
          : "";

    setSort(sortField ? [`${sorter?.field},${sortField}`] : undefined);
  };

  const locale = {
    emptyText: (
      <span>
        <p>
          <img
            className="image-empty-data"
            src="/images/empty-img-gray.png"
            alt="empty-img"
          ></img>
        </p>
        <p className="nodata-text">{t("Không tìm thấy", "Không tìm thấy")}</p>
      </span>
    ),
    triggerDesc: hoverSortMessage.triggerDesc,
    triggerAsc: hoverSortMessage.triggerAsc,
    cancelSort: t("Huỷ sắp xếp", "Huỷ sắp xếp"),
  };

  const dataColumns: TableColumnsType<SessionInfo> = [
    {
      title: "STT",
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      align: "center",
      width: "5%",
      render: (_: any, record, index: number) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      title: t("Tên học viên", "Tên học viên"),
      dataIndex: "studentFullname",
      key: "studentFullname",
      sorter: true,
      onHeaderCell: () => {
        return {
          onMouseEnter: () => {
            setHoverSortMessage({
              triggerDesc: t("Sắp xếp theo thứ tự Z-A", "Sắp xếp theo thứ tự Z-A"),
              triggerAsc: t("Sắp xếp theo thứ tự A-Z", "Sắp xếp theo thứ tự A-Z"),
            });
          },
        };
      },
    },
    {
      title: "Email",
      dataIndex: "studentUserEmail",
      key: "studentUserEmail",
      sorter: true,
      onHeaderCell: () => {
        return {
          onMouseEnter: () => {
            setHoverSortMessage({
              triggerDesc: t("Sắp xếp theo thứ tự Z-A", "Sắp xếp theo thứ tự Z-A"),
              triggerAsc: t("Sắp xếp theo thứ tự A-Z", "Sắp xếp theo thứ tự A-Z"),
            });
          },
        };
      },
    },
    {
      title: t("Ngày nộp", "Ngày nộp"),
      dataIndex: "submitTime",
      key: "submitTime",
      sorter: true,
      onHeaderCell: () => {
        return {
          onMouseEnter: () => {
            setHoverSortMessage({
              triggerDesc: t("Sắp xếp thứ tự mới đến cũ", "Sắp xếp thứ tự mới đến cũ"),
              triggerAsc: t("Sắp xếp theo cũ đến mới", "Sắp xếp theo cũ đến mới"),
            });
          },
        };
      },
      render: (_: any, record) =>
        moment(record.submitTime).format("DD/MM/YYYY"),
    },
    {
      title: t("Lần nộp", "Lần nộp"),
      dataIndex: "submitIndex",
      key: "submitIndex",
      width: "6%",
    },
    {
      title: t("Trạng thái", "Trạng thái"),
      dataIndex: "status",
      key: "status",
      render: (_: any, record) => {
        return (
          <div className="flex align-center">
            {displayStatus(record.status)}
          </div>
        );
      },
    },
    {
      title: t("Lớp", "Lớp"),
      dataIndex: "enrolledClassName",
      key: "enrolledClassName",
      width: "5%"
    },
    {
      title: t("Người chấm", "Người chấm"),
      dataIndex: "markedUserFullname",
      key: "markedUserFullname",
      render: (_: any, record) => record.markedUserFullname || "_",
    },
    {
      title: "",
      key: "scoring",
      render: (_: any, record) => {
        return (
          <Link
            className="text-primary"
            to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.evaluationAndDiscussion}/${blockId}/exercise/${record?.sessionId}`}
            state={{
              courseName: courseName,
              courseId: courseId,
              unitName: unitName,
              blockId: blockId,
              studentName: record.studentFullname,
              edit: true,
            }}
          >
            {t("Chấm điểm", "Chấm điểm")}
          </Link>
        );
      },
    },
    {
      fixed: "right",
      key: "view",
      width: "5%",
      render: (_: any, record) => {
        return (
          <Button
            className="view-btn"
            onClick={() =>
              navigate(`${routesConfig.courseManagement}/${courseId}/${routesConfig.evaluationAndDiscussion}/${blockId}/exercise/${record?.sessionId}`
                , {
                  state: {
                    courseName: courseName,
                    courseId: courseId,
                    unitName: unitName,
                    blockId: blockId,
                    studentName: record.studentFullname,
                  },
                })
            }
          >
            <EyeOutlined />
          </Button>
        );
      },
    },
  ];

  const displayStatus = (status: number | string) => {
    switch (status) {
      case ScoringStatusExam.SCORING:
        return (
          <Flex align="center">
            {" "}
            <div className="dot blue"></div>{t("Đang chấm", "Đang chấm")}
          </Flex>
        );
      case ScoringStatusExam.COMPLETED:
        return (
          <Flex align="center">
            <div className="dot green"></div>{t("Hoàn thành", "Hoàn thành")}
          </Flex>
        );
      case ScoringStatusExam.WAITING:
        return (
          <Flex align="center">
            <div className="dot gray"></div>{t("Đợi chấm", "Đợi chấm")}
          </Flex>
        );
      default:
        break;
    }
  };

  const breadcrumb = [
    {
      title: (
        <Link to={`${routesConfig.courseManagement}/${routesConfig.courseList}`}>{t("Danh sách khoá học", "Danh sách khoá học")}</Link>
      ),
    },
    {
      title: (
        <Link
          to={`${routesConfig.courseManagement}/${courseId}/${routesConfig.gradingList}`}
          state={{
            name: courseName,
          }}
        >
          {courseName}
        </Link>
      ),
    },
    {
      title: unitName,
    },
  ];

  return (
    <Layout className="page-header-group">
      <Form.Provider>
        <div className="scoring-course scoring-course-list w-full">
          <div className="scoring-page-header">
            <Breadcrumb items={breadcrumb} className="mb-1" />
            <div className="flex justify-between align-center gap-8">
              <div className="flex gap-8 align-center flex-1">
                <ArrowLeftOutlined
                  className="arrowleft-icon"
                  onClick={() => navigate(-1)}
                />
                <h3 className="page-title">{title}</h3>
              </div>
              <Button onClick={() => setIsShowFilter(!isShowFilter)}>
                {t("Bộ lọc", "Bộ lọc")}{" "}
                <DownOutlined
                  className={`mooc-arrow ${isShowFilter ? "down-arrow" : "up-arrow"
                    }`}
                />
              </Button>
              <div className="search-input">
                <FormItemInput
                  style={{ width: 280 }}
                  placeholder={t("Nhập tên học viên", "Nhập tên học viên")}
                  value={searchValue}
                  onChange={handleChangeInput}
                  onKeyPress={handleSearch}
                  afterPrefixIcon={
                    <SearchOutlined onClick={handleSubmitSearch} />
                  }
                />
              </div>
              {displayRoleAction && (
                <Button
                  onClick={() =>
                    navigate(
                      `${routesConfig.scoringListCourse}/${routesConfig.managementScoreAuto}/${blockId}`
                    )
                  }
                >
                  {t("Thao tác", "Thao tác")}
                </Button>
              )}
            </div>
            {isShowFilter && <FilterSearch onSearch={handleFilter} />}
          </div>
          <div className="scoring-content">
            <div className="description">
              <div className="txt">
                <span className="title">{t("Kết quả ghi nhận:", "Kết quả ghi nhận:")} </span>
                {(!scoreConfig?.recordResult && scoreConfig?.automaticScoring) ? t("Kết quả cuối cùng", "Kết quả cuối cùng") : RenderRecordedResults(scoreConfig?.recordResult)}
              </div>
              <div className="txt">
                <span className="title">{t("Điểm tối thiểu:", "Điểm tối thiểu:")} </span>
                {scoreConfig?.courseScore ? scoreConfig.courseScore : '0'}
              </div>
              <div className="txt">
                <span className="title">{t("Bắt buộc:", "Bắt buộc:")} </span>
                {scoreConfig?.recordResult !== null ? t("Có", "Có") : t("Không", "Không")}
              </div>
              <div className="txt">
                <span className="title">{t("Số lần được phép làm lại tối đa:", "Số lần được phép làm lại tối đa:")} </span>
                {scoreConfig?.numberActionEvaluate ?? '-'}
              </div>
              <div className="txt">
                <span className="title">{t("Hình thức đánh giá:", "Hình thức đánh giá:")} </span>
                {scoreConfig?.formOfReview === 1 ? t("Trực tuyến", "Trực tuyến") : t("Trực tiếp", "Trực tiếp")}
              </div>
            </div>
            <TableData
              dataTable={sessions?.map((item: SessionInfo, index) => ({
                ...item,
                id: item.sessionId,
              }))}
              dataColumns={dataColumns}
              scroll={{ x: 1000, y: 600 }}
              pagination
              localeProp={locale}
              pageNumber={currentPage}
              pageSize={pageSize}
              loadingTable={loading}
              totalRecords={totalElements}
              handleTableChangeProps={handleTableChange}
              setPageNumber={setCurrentPage}
              setPageSize={setPageSize}
            />
          </div>
        </div>
      </Form.Provider>
    </Layout>
  );
}

export default ScoringList;
